import React, { useState, useMemo } from 'react';
import { getProgramById } from '../data/programs';
import { saveSettings } from '../utils/settings';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const EXTRA_OPTIONS = ['Accessories', 'Cardio'];

export default function ScheduleTab({ settings, progress, refresh }) {
  const [editing, setEditing] = useState(false);
  const [editDayIdx, setEditDayIdx] = useState(null); // which day is being edited
  const [editExtras, setEditExtras] = useState({}); // temp extras state

  const activePrograms = useMemo(() => {
    return ['bench', 'squat', 'deadlift'].map(lift => ({
      lift,
      program: getProgramById(settings[`${lift}Program`]),
      progress: progress[lift] || { weekIndex: 0, dayIndex: 0, cycle: 1 },
    })).filter(p => p.program);
  }, [settings, progress]);

  // Get default day slots based on frequency (used when no custom assignment)
  const getDefaultDaySlots = (lift, freq) => {
    if (freq === 1) return lift === 'bench' ? [2] : lift === 'squat' ? [0] : [4];
    if (freq === 2) return lift === 'bench' ? [2, 4] : lift === 'squat' ? [0, 2] : [0, 4];
    if (freq === 3) return [0, 2, 4];
    return [0];
  };

  // Get current day assignments (custom or default)
  const dayAssignments = useMemo(() => {
    const custom = settings.trainingDays;
    const assignments = {};
    for (const { lift, program } of activePrograms) {
      const week = program.weeks[0]; // Use week structure for frequency
      if (!week) continue;
      const freq = week.days.length;
      if (custom && custom[lift]) {
        assignments[lift] = custom[lift].slice(0, freq);
      } else {
        assignments[lift] = getDefaultDaySlots(lift, freq);
      }
    }
    return assignments;
  }, [activePrograms, settings.trainingDays]);

  // Build schedule grid
  const schedule = useMemo(() => {
    const grid = DAY_NAMES.map((name, i) => ({ name, idx: i, sessions: [], extras: [] }));

    for (const { lift, program, progress: prog } of activePrograms) {
      const week = program.weeks[prog.weekIndex];
      if (!week) continue;
      const slots = dayAssignments[lift] || [];

      week.days.forEach((day, dayIdx) => {
        const slotIdx = slots[dayIdx];
        if (slotIdx === undefined || !grid[slotIdx]) return;
        grid[slotIdx].sessions.push({
          lift,
          program: program.displayName,
          weekNum: week.weekNum,
          dayNum: day.dayNum,
          isNext: dayIdx === prog.dayIndex,
          isMaxTest: week.isMaxTest,
        });
      });
    }

    // Add extras (cardio, accessories)
    const extras = settings.scheduleExtras || {};
    for (const [dayStr, items] of Object.entries(extras)) {
      const dayIdx = parseInt(dayStr);
      if (grid[dayIdx] && items && items.length > 0) {
        grid[dayIdx].extras = items;
      }
    }

    return grid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePrograms, dayAssignments, settings.scheduleExtras]);

  // === Edit handlers ===
  const startEditing = () => {
    setEditing(true);
    setEditExtras(settings.scheduleExtras || {});
  };

  const handleDayTap = (dayIdx) => {
    setEditDayIdx(editDayIdx === dayIdx ? null : dayIdx);
  };

  const toggleLiftOnDay = (lift, dayIdx) => {
    const current = settings.trainingDays || {};
    const liftDays = current[lift] ? [...current[lift]] : [...(dayAssignments[lift] || [])];
    const existingIdx = liftDays.indexOf(dayIdx);

    if (existingIdx >= 0) {
      liftDays.splice(existingIdx, 1);
    } else {
      // Find the right insertion position to maintain order
      liftDays.push(dayIdx);
      liftDays.sort((a, b) => a - b);
    }

    const updated = { ...current, [lift]: liftDays };
    // Check frequency constraint
    const { program } = activePrograms.find(p => p.lift === lift) || {};
    if (program) {
      const freq = program.weeks[0]?.days?.length || 1;
      if (liftDays.length > freq) return; // Can't exceed program frequency
    }

    saveSettings({ trainingDays: updated });
    refresh();
  };

  const toggleExtraOnDay = (dayIdx, extra) => {
    const updated = { ...editExtras };
    const dayKey = String(dayIdx);
    if (!updated[dayKey]) updated[dayKey] = [];
    const idx = updated[dayKey].indexOf(extra);
    if (idx >= 0) {
      updated[dayKey] = updated[dayKey].filter(e => e !== extra);
    } else {
      updated[dayKey] = [...updated[dayKey], extra];
    }
    setEditExtras(updated);
    saveSettings({ scheduleExtras: updated });
    refresh();
  };

  const stopEditing = () => {
    setEditing(false);
    setEditDayIdx(null);
  };

  const liftColors = { bench: 'var(--bench-color)', squat: 'var(--squat-color)', deadlift: 'var(--deadlift-color)' };
  const extraColors = { Accessories: '#c77dff', Cardio: '#ff4a6a' };

  return (
    <div>
      <div className="session-header">
        <h1>Schedule</h1>
        <p className="sub">Your weekly training plan</p>
      </div>

      {/* Active programs */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">Active Programs</div>
        {activePrograms.map(({ lift, program, progress: prog }) => {
          const week = program.weeks[prog.weekIndex];
          return (
            <div key={lift} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '8px 0', borderBottom: '1px solid var(--border)',
            }}>
              <div>
                <span style={{ color: liftColors[lift], fontWeight: 600, fontFamily: 'Barlow Condensed, sans-serif', fontSize: 15, textTransform: 'uppercase' }}>
                  {lift}
                </span>
                <span style={{ color: 'var(--text-dim)', fontSize: 13, marginLeft: 8 }}>
                  {program.displayName}
                </span>
              </div>
              <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 13, color: 'var(--text-dim)' }}>
                {week ? `W${week.weekNum} D${prog.dayIndex + 1}/${week.days.length}` : 'Complete'}
                {week?.isMaxTest && <span style={{ color: 'var(--max-test-border)', marginLeft: 6 }}>MAX</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit toggle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div className="card-header" style={{ marginBottom: 0 }}>This Week</div>
        <button style={{
          background: 'none', border: 'none', color: editing ? 'var(--success)' : 'var(--accent)',
          fontSize: 12, cursor: 'pointer', fontFamily: 'Barlow Condensed, sans-serif',
          textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600,
        }} onClick={editing ? stopEditing : startEditing}>
          {editing ? 'Done' : 'Edit'}
        </button>
      </div>

      {editing && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
          Tap a day to assign lifts, cardio, or accessories
        </div>
      )}

      {/* Weekly grid */}
      {schedule.map((day, i) => {
        const hasNext = day.sessions.some(s => s.isNext);
        const isEditing = editing && editDayIdx === i;

        return (
          <div key={i}>
            <div className={`schedule-day ${hasNext ? 'next' : ''}`}
              onClick={editing ? () => handleDayTap(i) : undefined}
              style={editing ? { cursor: 'pointer' } : {}}>
              <span className="schedule-day-name">{day.name}</span>
              <div style={{ flex: 1, marginLeft: 12 }}>
                {day.sessions.length === 0 && day.extras.length === 0 ? (
                  <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    {editing ? 'Tap to add' : 'Rest'}
                  </span>
                ) : (
                  <>
                    {day.sessions.map((s, j) => (
                      <div key={j} className="schedule-session" style={{ marginBottom: j < day.sessions.length - 1 || day.extras.length > 0 ? 4 : 0 }}>
                        <span className="schedule-session-lift" style={{ color: liftColors[s.lift] }}>
                          {s.lift.charAt(0).toUpperCase() + s.lift.slice(1)}
                        </span>
                        {' '}W{s.weekNum} D{s.dayNum}
                        {s.isMaxTest && <span style={{ color: 'var(--max-test-border)', fontSize: 11, marginLeft: 4 }}>MAX</span>}
                        {s.isNext && <span className="next-badge">NEXT</span>}
                      </div>
                    ))}
                    {day.extras.map((extra, j) => (
                      <div key={`extra-${j}`} style={{ fontSize: 13 }}>
                        <span style={{ color: extraColors[extra] || 'var(--text-dim)', fontWeight: 600 }}>
                          {extra}
                        </span>
                      </div>
                    ))}
                  </>
                )}
              </div>
              {editing && (
                <span style={{ color: 'var(--text-muted)', fontSize: 16 }}>
                  {isEditing ? '−' : '+'}
                </span>
              )}
            </div>

            {/* Edit panel for this day */}
            {isEditing && (
              <div style={{
                background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                borderRadius: 8, padding: 12, marginBottom: 6, marginTop: -2,
              }}>
                <div style={{ fontSize: 12, color: 'var(--text-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Lifts on {day.name}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                  {activePrograms.map(({ lift, program }) => {
                    const isAssigned = (dayAssignments[lift] || []).includes(i);
                    const freq = program.weeks[0]?.days?.length || 1;
                    const currentCount = (dayAssignments[lift] || []).length;
                    const canAdd = !isAssigned && currentCount < freq;
                    return (
                      <button key={lift} onClick={() => {
                        if (isAssigned || canAdd) toggleLiftOnDay(lift, i);
                      }} style={{
                        padding: '6px 12px', borderRadius: 6,
                        border: `1px solid ${isAssigned ? liftColors[lift] : 'var(--border)'}`,
                        background: isAssigned ? `${liftColors[lift]}20` : 'none',
                        color: isAssigned ? liftColors[lift] : canAdd ? 'var(--text-dim)' : 'var(--text-muted)',
                        fontFamily: 'Barlow Condensed, sans-serif', fontSize: 13,
                        fontWeight: 600, textTransform: 'uppercase', cursor: (isAssigned || canAdd) ? 'pointer' : 'default',
                        opacity: (!isAssigned && !canAdd) ? 0.4 : 1,
                      }}>
                        {lift}
                        {isAssigned && ' ✓'}
                        {!isAssigned && !canAdd && ` (${freq}/${freq})`}
                      </button>
                    );
                  })}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Extras
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {EXTRA_OPTIONS.map(extra => {
                    const isActive = (editExtras[String(i)] || []).includes(extra);
                    return (
                      <button key={extra} onClick={() => toggleExtraOnDay(i, extra)} style={{
                        padding: '6px 12px', borderRadius: 6,
                        border: `1px solid ${isActive ? extraColors[extra] : 'var(--border)'}`,
                        background: isActive ? `${extraColors[extra]}20` : 'none',
                        color: isActive ? extraColors[extra] : 'var(--text-dim)',
                        fontFamily: 'Barlow Condensed, sans-serif', fontSize: 13,
                        fontWeight: 600, cursor: 'pointer',
                      }}>
                        {extra}
                        {isActive && ' ✓'}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Full cycle per lift */}
      {activePrograms.map(({ lift, program, progress: prog }) => (
        <div key={lift} style={{ marginTop: 20 }}>
          <div className="card-header" style={{ color: liftColors[lift] }}>
            {lift} — Full Cycle
          </div>
          <div className="table-scroll">
            <table>
              <thead>
                <tr><th>Week</th><th>Day</th><th>Status</th></tr>
              </thead>
              <tbody>
                {program.weeks.map((week, wi) =>
                  week.days.map((day, di) => {
                    const isCurrent = wi === prog.weekIndex && di === prog.dayIndex;
                    const isDone = wi < prog.weekIndex || (wi === prog.weekIndex && di < prog.dayIndex);
                    return (
                      <tr key={`${wi}-${di}`} style={{
                        opacity: isDone ? 0.5 : 1,
                        background: isCurrent ? 'var(--bg-elevated)' : 'none',
                      }}>
                        <td>W{week.weekNum}{week.isMaxTest ? ' (Max)' : ''}</td>
                        <td>Day {day.dayNum}</td>
                        <td>
                          {isDone && <span style={{ color: 'var(--success)' }}>✓</span>}
                          {isCurrent && <span className="next-badge">NEXT</span>}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
